/**
 * JsonRedlineRenderer — Word 红线审查渲染引擎
 *
 * 接收大模型通过 Zod Tool 输出的结构化 Word JSON，
 * 在纯前端以 A4 纸拟物形态渲染出带删除线、高亮和批注气泡的专业审查界面。
 */

import React, { useState, useRef, useEffect } from 'react';

// ── 类型定义 ──
export interface WordRun {
  text: string;
  bold?: boolean;
  italic?: boolean;
  underline?: boolean;
  strikethrough?: boolean;
  highlight?: string; // 'yellow' | 'green' | 'red' | ...
  comment?: string;
  _commentIndex?: number; // 内部渲染使用的序号
}

export interface WordSection {
  type: 'heading1' | 'heading2' | 'heading3' | 'paragraph' | 'list_item';
  text?: string;       // 简单模式（向后兼容）
  runs?: WordRun[];    // 高级模式（Run 级控制）
  bold?: boolean;
}

export interface WordDocumentJson {
  title: string;
  sections: WordSection[];
}

// ── 高亮颜色表 ──
const HIGHLIGHT_COLORS: Record<string, string> = {
  yellow: 'bg-yellow-200 text-gray-900',
  green: 'bg-green-200 text-gray-900',
  red: 'bg-red-200 text-gray-900',
  blue: 'bg-blue-200 text-gray-900',
  cyan: 'bg-cyan-200 text-gray-900',
  magenta: 'bg-pink-200 text-gray-900',
};

// ── 批注气泡组件 ──
const CommentBubble: React.FC<{ comment: string; index: number }> = ({ comment, index }) => {
  const [visible, setVisible] = useState(false);
  const ref = useRef<HTMLSpanElement>(null);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setVisible(false);
    };
    if (visible) document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [visible]);

  return (
    <span ref={ref} className="relative inline-block">
      <button
        onClick={() => setVisible(!visible)}
        className="inline-flex items-center justify-center ml-0.5 w-5 h-5 text-[10px] font-bold rounded-full bg-amber-400 text-white cursor-pointer hover:bg-amber-500 transition-colors shadow-sm"
        title="查看批注"
        style={{ verticalAlign: 'super', lineHeight: 1 }}
      >
        {index}
      </button>
      {visible && (
        <div
          className="absolute z-50 left-6 -top-2 w-64 p-3 rounded-lg shadow-xl border border-amber-200 bg-amber-50 text-sm text-gray-800"
          style={{ animation: 'fadeIn 0.15s ease-out' }}
        >
          <div className="flex items-center gap-1.5 mb-1.5 text-xs font-semibold text-amber-700">
            <span>💬</span> AI 审查批注
          </div>
          <p className="leading-relaxed">{comment}</p>
        </div>
      )}
    </span>
  );
};

// ── 单个 Run 渲染 ──
const RunRenderer: React.FC<{ run: WordRun }> = ({ run }) => {
  let el: React.ReactNode = <>{run.text}</>;

  // 1. 高亮
  if (run.highlight) {
    const cls = HIGHLIGHT_COLORS[run.highlight] || HIGHLIGHT_COLORS.yellow;
    el = <mark className={`${cls} rounded-sm px-0.5`}>{el}</mark>;
  }

  // 2. 删除线
  if (run.strikethrough) {
    el = <del className="line-through text-red-500 bg-red-50 decoration-red-400 decoration-2">{el}</del>;
  }

  // 3. 加粗
  if (run.bold) el = <strong>{el}</strong>;

  // 4. 斜体
  if (run.italic) el = <em>{el}</em>;

  // 5. 下划线
  if (run.underline) el = <u>{el}</u>;

  // 6. 批注
  if (run.comment) {
    el = (
      <span className="border-b-2 border-dashed border-amber-400">
        {el}
        <CommentBubble comment={run.comment} index={run._commentIndex || 1} />
      </span>
    );
  }

  return <span>{el}</span>;
};

// ── 段落/标题渲染 ──
const SectionRenderer: React.FC<{ section: WordSection }> = ({ section }) => {
  // 如果有 runs，则逐 run 渲染；否则兼容旧的 text 字段
  const content = section.runs ? (
    section.runs.map((run, i) => <RunRenderer key={i} run={run} />)
  ) : (
    <span className={section.bold ? 'font-bold' : ''}>{section.text}</span>
  );

  switch (section.type) {
    case 'heading1':
      return <h1 className="text-xl font-bold mt-6 mb-3 text-center text-gray-900">{content}</h1>;
    case 'heading2':
      return <h2 className="text-lg font-semibold mt-5 mb-2 text-gray-800">{content}</h2>;
    case 'heading3':
      return <h3 className="text-base font-semibold mt-4 mb-1.5 text-gray-700">{content}</h3>;
    case 'list_item':
      return <li className="ml-6 mb-1 list-disc text-gray-800">{content}</li>;
    case 'paragraph':
    default:
      return <p className="mb-2 text-gray-800" style={{ textIndent: '2em', textAlign: 'justify' }}>{content}</p>;
  }
};

// ── 主组件 ──
interface JsonRedlineRendererProps {
  document: WordDocumentJson;
  onExport?: () => void;
}

const JsonRedlineRenderer: React.FC<JsonRedlineRendererProps> = ({ document, onExport }) => {
  // 预处理分配批注序号，避免 React 渲染阶段修改全局变量的反模式
  const processedDocument = React.useMemo(() => {
    let counter = 1;
    const newSections = document.sections.map(sec => {
      if (!sec.runs) return sec;
      const newRuns = sec.runs.map(run => {
        if (run.comment) {
          return { ...run, _commentIndex: counter++ };
        }
        return run;
      });
      return { ...sec, runs: newRuns };
    });
    return { ...document, sections: newSections };
  }, [document]);

  return (
    <div className="my-4 flex justify-center">
      <div
        className="relative bg-white rounded-sm border border-gray-200"
        style={{
          maxWidth: '720px',
          width: '100%',
          padding: '56px 64px',
          fontFamily: '"Noto Serif SC", "Georgia", "Times New Roman", serif',
          fontSize: '14px',
          lineHeight: '1.8',
          boxShadow: '0 4px 24px rgba(0,0,0,0.10), 0 1px 4px rgba(0,0,0,0.06)',
        }}
      >
        {/* 导出按钮 */}
        {onExport && (
          <button
            onClick={onExport}
            className="absolute top-3 right-3 flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-blue-700 bg-blue-50 border border-blue-200 rounded-md hover:bg-blue-100 transition-colors"
          >
            ⬇️ 导出 Word
          </button>
        )}

        {/* 标题 */}
        <h1
          className="text-center font-bold text-gray-900 mb-6 pb-4 border-b border-gray-200"
          style={{ fontSize: '20px' }}
        >
          {document.title}
        </h1>

        {/* 正文 Sections */}
        {processedDocument.sections.map((section, i) => (
          <SectionRenderer key={i} section={section} />
        ))}
      </div>
    </div>
  );
};

export default JsonRedlineRenderer;
